
import Vue from 'vue';
import isMobileView from '@root/common/utils/isMobileView';
import { PodcastCategory } from '@root/modules/podcast/types/podcast';
import ContentImage from '@core/components/UI/ContentImage.vue';
import LinkHandler from '@core/components/helpers/LinkHandler.vue';
import categoryUrl from '@root/modules/category/utils/buildCategoryUrl';
import { CustomCrop } from '@root/common/types/fragment';
import getItemFromObject from 'lodash/get';

interface Srcset {
  w?: number;
  h?: number;
  r?: string;
}

interface PictureSrcset {
  xs: Srcset;
  sm: Srcset;
  lg: Srcset;
}

interface Props {
  category: PodcastCategory;
}

interface Data {
  isMobileView: boolean;
}

interface Methods {
  getCategoryHref: (category: PodcastCategory) => string;
  getCropperData(index?: number): Record<string, unknown> | null;
  getCustomCrop(ratio: string, index?: number): CustomCrop | undefined;
  getFocuspoint(index?: number): Record<string, unknown>;
  getItemFromObject: typeof getItemFromObject;
}

interface Computed {
  isBook: boolean;
}

const pictureSrcset: PictureSrcset = { lg: { w: 450, h: 450, r: '1:1' }, sm: { w: 282, h: 282, r: '1:1' }, xs: { w: 120, h: 120, r: '1:1' } };
const bookPictureSrcset: PictureSrcset = { lg: { w: 450, h: 674, r: '2:3' }, sm: { w: 282, h: 423, r: '2:3' }, xs: { w: 120, h: 180, r: '2:3' } };
const sizeList: ['lg', 'sm', 'xs'] = ['lg', 'sm', 'xs'];

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ContentImage,
    LinkHandler,
  },
  props: {
    category: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isMobileView: false,
    };
  },
  computed: {
    isBook() {
      return this.category.parentCategory?.parentCategory?.id === 96603927;
    },
  },
  methods: {
    getCategoryHref(category) {
      return categoryUrl({ category, domain: 'www.delfi.lt/fone', fakeDomainEnabled: this.$config.FAKE_DOMAIN_ENABLED });
    },
    getCustomCrop(ratio) {
      const image = this.category.attributes?.image;
      if (!image) {
        return undefined;
      }

      const customCrops = image!.attributes?.cropperData?.customCrops || [];

      return customCrops.find((crop: { ratio: string }) => crop.ratio === ratio);
    },
    getFocuspoint() {
      const cropperProps: Record<string, unknown> = {};

      const image = this.category.attributes?.image;

      const focuspoint = image?.attributes?.cropperData?.focuspoint;
      if (focuspoint?.x !== undefined && focuspoint?.x !== null) {
        cropperProps.fx = focuspoint.x;
      } else {
        cropperProps.fx = '0.5';
      }

      if (focuspoint?.y !== undefined && focuspoint?.y !== null) {
        cropperProps.fy = focuspoint.y;
      } else {
        cropperProps.fy = '0.5';
      }

      return cropperProps;
    },
    getCropperData() {
      if (!this.category) {
        return null;
      }

      const cropperData: Record<string, unknown> = {};

      for (const size of sizeList) {
        const _pictureSrcset = this.isBook ? bookPictureSrcset : pictureSrcset;
        if (_pictureSrcset?.[size]) {
          const sizeSrcset = _pictureSrcset[size];

          const customCrop = this.getCustomCrop(sizeSrcset?.r || '1:1');

          if (customCrop) {
            cropperData[size] = { ...sizeSrcset, customCrop };
          } else {
            cropperData[size] = sizeSrcset;
          }
        }
      }

      const image = this.category.attributes?.image;
      if (!image) {
        return null;
      }

      return {
        ...cropperData,
        ...(image!.attributes?.cropperData || {}),
        base: this.getFocuspoint(),
      };
    },
    getItemFromObject,
  },
  mounted() {
    this.isMobileView = isMobileView();
  },
});
